:root {
  color-scheme: dark;
  --night: #07080a;
  --hull: #12161c;
  --cream: #eef3f8;
  --mist: #c8d6e3;
  --dust: #b3c4d4;
  --steel: #8aa6bc;
  --sith: #c4001a;
  --fg: #eef3f8;
  --glass: rgba(12, 16, 22, 0.78);
  --line: rgba(138, 166, 188, 0.28);
  --on-accent: #eef3f8;
  --btn-fill: rgba(18, 22, 28, 0.82);
  --glow: 196, 0, 26;
}

html {
  scroll-behavior: smooth;
}

html,
body,
#root {
  min-height: 100%;
}

#main,
#top {
  scroll-margin-top: 5rem;
}

body {
  margin: 0;
  background: var(--night);
  color: var(--fg);
}

#root {
  isolation: isolate;
}

:focus {
  outline: none;
}

:focus-visible {
  outline: 2px solid var(--sith);
  outline-offset: 3px;
}

.atmosphere {
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  background:
    radial-gradient(ellipse 50% 40% at 72% 18%, rgba(196, 0, 26, 0.12), transparent 52%),
    radial-gradient(ellipse 60% 45% at 18% 80%, rgba(138, 166, 188, 0.1), transparent 55%);
}

.atmosphere__stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1px 1px at 8% 12%, rgba(238, 243, 248, 0.7) 50%, transparent 51%),
    radial-gradient(1px 1px at 22% 38%, rgba(238, 243, 248, 0.45) 50%, transparent 51%),
    radial-gradient(1.5px 1.5px at 41% 18%, rgba(238, 243, 248, 0.55) 50%, transparent 51%),
    radial-gradient(1px 1px at 63% 72%, rgba(238, 243, 248, 0.4) 50%, transparent 51%),
    radial-gradient(1px 1px at 77% 28%, rgba(238, 243, 248, 0.65) 50%, transparent 51%),
    radial-gradient(1px 1px at 91% 61%, rgba(238, 243, 248, 0.35) 50%, transparent 51%),
    radial-gradient(1.5px 1.5px at 14% 81%, rgba(238, 243, 248, 0.5) 50%, transparent 51%),
    radial-gradient(1px 1px at 52% 8%, rgba(238, 243, 248, 0.4) 50%, transparent 51%);
}

.atmosphere__station {
  position: absolute;
  width: min(72vw, 40rem);
  right: -8%;
  top: 2%;
  filter: drop-shadow(0 0 60px rgba(196, 0, 26, 0.18));
}

.hero-stage {
  display: grid;
  place-items: center;
  min-height: min(58vw, 26rem);
}

.hero-stage .wordmark {
  position: relative;
  z-index: 1;
  text-align: center;
}

.atmosphere__destroyer {
  position: absolute;
  width: min(78vw, 46rem);
  left: -10%;
  bottom: 5%;
  opacity: 0.72;
  filter: drop-shadow(0 12px 40px rgba(0, 0, 0, 0.55));
}

.atmosphere__falcon,
.atmosphere__xwing {
  display: none;
  position: absolute;
}

.atmosphere__falcon {
  width: min(76vw, 44rem);
  height: auto;
  right: -6%;
  top: 4%;
  transform: rotate(10deg);
  transform-origin: center;
}

.atmosphere__xwing {
  width: min(78vw, 46rem);
  left: -8%;
  bottom: 6%;
  opacity: 0.86;
  overflow: visible;
}

.xwing__shot {
  opacity: 0;
  transform-box: view-box;
  transform-origin: center;
  filter: drop-shadow(0 0 3px #fff) drop-shadow(0 0 8px rgba(255, 0, 31, 0.95))
    drop-shadow(0 0 18px rgba(196, 0, 26, 0.8));
  animation: xwing-fire 6s cubic-bezier(0.17, 0.67, 0.25, 1) infinite;
}

.xwing__shot--two {
  animation-delay: 120ms;
}

.xwing__shot--three {
  animation-delay: 1.25s;
}

.xwing__shot--four {
  animation-delay: 1.37s;
}

.xwing__muzzle,
.xwing__arc {
  opacity: 0;
  filter: drop-shadow(0 0 4px #fff) drop-shadow(0 0 12px rgba(255, 0, 31, 0.95));
  animation: xwing-muzzle 6s steps(2, jump-none) infinite;
}

.xwing__muzzle--two,
.xwing__arc--two {
  animation-delay: 120ms;
}

.xwing__muzzle--three,
.xwing__arc--three {
  animation-delay: 1.25s;
}

.xwing__muzzle--four,
.xwing__arc--four {
  animation-delay: 1.37s;
}

@keyframes xwing-fire {
  0%,
  5% {
    opacity: 0;
    transform: translateX(-20px) scaleX(0.3);
  }

  6% {
    opacity: 1;
    transform: translateX(0) scaleX(0.9);
  }

  17% {
    opacity: 1;
    transform: translateX(940px) scaleX(1.35);
  }

  18%,
  100% {
    opacity: 0;
    transform: translateX(980px) scaleX(1.35);
  }
}

@keyframes xwing-muzzle {
  0%,
  5% {
    opacity: 0;
  }

  6%,
  9% {
    opacity: 1;
  }

  11%,
  100% {
    opacity: 0;
  }
}

.atmosphere__visor {
  position: absolute;
  width: min(28vw, 16rem);
  height: 4.5rem;
  left: 10%;
  top: 16%;
  background:
    radial-gradient(circle at 28% 50%, #c4001a 0 18%, transparent 42%),
    radial-gradient(circle at 72% 50%, #c4001a 0 18%, transparent 42%);
  filter: blur(10px);
  opacity: 0.45;
}

.atmosphere__grain {
  position: absolute;
  inset: 0;
  opacity: 0.1;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

.site {
  position: relative;
  z-index: 1;
  min-height: 100dvh;
}

.skip-link {
  position: absolute;
  left: 1rem;
  top: -4rem;
  z-index: 30;
  padding: 0.65rem 0.95rem;
  background: var(--cream);
  color: var(--night);
  text-decoration: none;
  font-weight: 600;
}

.skip-link:focus {
  top: 1rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.glass {
  background: var(--glass);
  border: 1px solid rgba(138, 166, 188, 0.2);
  border-radius: 1.15rem;
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.28);
}

.glass--nav {
  border-radius: 999px;
}

.site-nav {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  max-width: 72rem;
  margin: 0 auto;
  padding: 0.5rem 1rem;
}

.site-nav .brand-link {
  justify-self: start;
}

.site-nav__links {
  justify-self: end;
}

.theme-saber {
  justify-self: center;
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0.2rem 0.35rem;
  border: 0;
  background: transparent;
  appearance: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.theme-saber:focus-visible {
  outline: 2px solid var(--sith);
  outline-offset: 4px;
}

.theme-saber__track {
  display: flex;
  align-items: center;
  width: min(12rem, 34vw);
}

.theme-saber__hilt {
  width: 3.55rem;
  height: 1.35rem;
  flex-shrink: 0;
  z-index: 1;
}

.theme-saber__hilt--luke {
  display: none;
}

.theme-saber__blade {
  position: relative;
  flex: 1 1 auto;
  height: 8px;
  margin-left: -5px;
  border-radius: 0 999px 999px 0;
  background: linear-gradient(90deg, #8a0012 0%, #c4001a 18%, #ff8a96 100%);
  box-shadow:
    0 0 6px 1px rgba(255, 210, 214, 0.7),
    0 0 16px 5px rgba(196, 0, 26, 0.7),
    0 0 36px 10px rgba(196, 0, 26, 0.32);
  transform-origin: left center;
  animation: saber-flicker 2.8s ease-in-out infinite;
}

.theme-saber__core {
  position: absolute;
  inset: 28% 6%;
  border-radius: inherit;
  background: #fff;
  box-shadow: 0 0 6px 2px rgba(255, 255, 255, 0.85);
}

.theme-saber[data-phase='retract'] .theme-saber__blade {
  transform: scaleX(0.04);
  opacity: 0.15;
  transition:
    transform 300ms ease-in,
    opacity 220ms ease;
  animation: none;
}

.theme-saber[data-phase='flicker'] .theme-saber__blade {
  transform: scaleX(0.04);
  opacity: 0;
  animation: none;
}

.theme-saber[data-phase='extend'] .theme-saber__blade {
  animation: theme-blade-extend 420ms ease-out;
}

.theme-flicker {
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 80;
  opacity: 0;
}

.theme-flicker--on {
  animation: theme-flicker 560ms steps(4);
}

@keyframes theme-blade-extend {
  from {
    transform: scaleX(0.04);
    opacity: 0.2;
  }

  to {
    transform: scaleX(1);
    opacity: 1;
  }
}

@keyframes theme-flicker {
  0% {
    opacity: 0.1;
    background: #000;
  }

  18% {
    opacity: 0.88;
    background: #fff;
  }

  34% {
    opacity: 0.45;
    background: #05070a;
  }

  52% {
    opacity: 0.62;
    background: #c8e8ff;
  }

  70% {
    opacity: 0.28;
    background: #081018;
  }

  86% {
    opacity: 0.7;
    background: #f4fbff;
  }

  100% {
    opacity: 0;
    background: transparent;
  }
}

.wordmark {
  margin: 0;
  font-family: 'Syne Variable', Syne, sans-serif;
  font-weight: 800;
  letter-spacing: -0.07em;
  line-height: 0.8;
  text-transform: uppercase;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: keep-all;
  font-size: clamp(3.4rem, 14vw, 9.5rem);
  color: var(--cream);
  text-shadow: 0 0 48px rgba(196, 0, 26, 0.28);
}

.kicker {
  margin: 0 0 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--dust);
}

.topic-heading {
  max-width: 12ch;
}

.lede {
  margin: 0 0 2rem;
  font-family: 'Syne Variable', Syne, sans-serif;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.3;
  font-size: clamp(1.35rem, 3vw, 1.85rem);
  color: var(--cream);
  max-width: 24ch;
}

.prose {
  max-width: 38rem;
}

.prose p {
  margin: 0 0 1.25rem;
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--cream);
}

.hero-note {
  max-width: 28rem;
  padding: 1.35rem 1.5rem;
}

.soft {
  color: var(--dust);
}

.hero-note__floor {
  margin-top: 1rem;
  line-height: 1.6;
}

.doors {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.7rem;
  margin-top: 2.75rem;
}

.door {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 8.5rem;
  padding: 1.35rem 1.4rem;
  border-radius: 1.25rem;
  text-decoration: none;
  color: var(--cream);
  background: var(--glass);
  border: 1px solid rgba(138, 166, 188, 0.2);
  backdrop-filter: blur(18px);
  isolation: isolate;
  transition:
    transform 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;
}

.door::after {
  content: '';
  position: absolute;
  inset: auto -12% -30% auto;
  width: 55%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(196, 0, 26, 0.22), transparent 68%);
  z-index: 0;
}

.door > * {
  position: relative;
  z-index: 1;
}

.door:hover,
.door:focus-visible {
  transform: translateY(-3px);
  border-color: var(--sith);
  box-shadow: 0 16px 40px rgba(196, 0, 26, 0.16);
}

.door__idx {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--dust);
}

.door__label {
  font-family: 'Syne Variable', Syne, sans-serif;
  font-weight: 800;
  letter-spacing: -0.045em;
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 0.95;
}

.door--featured {
  min-height: 10rem;
}

.doors--hub {
  margin-top: 2rem;
}

.doors--hub .door {
  min-height: 11rem;
}

.doors--hub .door__label {
  font-size: clamp(3rem, 6vw, 4.4rem);
}

.topic-shell {
  position: relative;
  overflow: hidden;
}

.topic-shell::before {
  content: '';
  position: absolute;
  width: min(52vw, 28rem);
  aspect-ratio: 1;
  right: -10%;
  top: -12%;
  border-radius: 50%;
  border: 1px solid rgba(138, 166, 188, 0.16);
  box-shadow: inset 0 0 80px rgba(196, 0, 26, 0.12);
  pointer-events: none;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.5rem 1.2rem;
  border: 1px solid rgba(138, 166, 188, 0.35);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  background: var(--btn-fill);
  color: var(--fg);
  text-decoration: none;
  font-family: 'Manrope Variable', Manrope, sans-serif;
  font-size: 0.92rem;
  font-weight: 650;
  letter-spacing: 0.03em;
  line-height: 1;
  transition:
    background 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease;
}

.btn:hover,
.btn:focus-visible {
  background: var(--sith);
  border-color: var(--sith);
  color: var(--on-accent);
  box-shadow: 0 0 24px rgba(var(--glow), 0.35);
}

.btn[aria-current='page'] {
  background: var(--sith);
  border-color: var(--sith);
  color: var(--on-accent);
}

.btn--nav {
  min-height: 2.35rem;
  padding: 0.4rem 0.85rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 600;
}

.nav-group {
  position: relative;
}

.nav-group > summary {
  list-style: none;
  cursor: pointer;
}

.nav-group > summary::-webkit-details-marker {
  display: none;
}

.nav-group--current > summary,
.nav-group--current > .btn--nav,
.nav-group[open] > summary {
  background: var(--sith);
  border-color: var(--sith);
  color: var(--on-accent);
}

.nav-group:not(details) > .nav-group__menu {
  display: none;
}

.nav-group:not(details):hover > .nav-group__menu,
.nav-group:not(details):focus-within > .nav-group__menu {
  display: flex;
}

.nav-group__menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 12;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  min-width: 8.5rem;
  padding: 0.45rem;
  background: var(--glass);
  border: 1px solid rgba(138, 166, 188, 0.22);
  backdrop-filter: blur(18px);
}

.nav-group__menu .btn--nav {
  justify-content: flex-start;
}

.brand-link {
  display: inline-flex;
  align-items: center;
  min-height: 2.35rem;
  padding: 0 0.4rem;
  color: var(--fg);
  text-decoration: none;
}

.brand-link:hover,
.brand-link:focus-visible {
  color: var(--sith);
}

.pager {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 3.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(138, 166, 188, 0.22);
}

.topic-shell--projects {
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 2.5rem;
}

.projects-rail {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  gap: 1rem;
  min-height: 22rem;
  padding: 1.15rem 0.85rem 1.15rem 1.25rem;
  order: -1;
}

.projects-rail::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(138, 166, 188, 0.35);
  clip-path: polygon(28px 0, 100% 0, 100% calc(100% - 28px), calc(100% - 28px) 100%, 0 100%, 0 28px);
}

.projects-rail > * {
  position: relative;
  z-index: 1;
}

.projects-rail__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.75rem;
  text-align: right;
  padding: 1.25rem 0;
}

.projects-rail__title {
  margin: 0;
  font-family: 'Syne Variable', Syne, sans-serif;
  font-weight: 800;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  font-size: 0.72rem;
  color: var(--dust);
}

.projects-rail__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
}

.projects-rail__link {
  display: block;
  max-width: 14rem;
  margin-left: auto;
  color: var(--fg);
  text-decoration: none;
  font-family: 'Syne Variable', Syne, sans-serif;
  font-weight: 750;
  letter-spacing: -0.03em;
  line-height: 1.2;
  font-size: 1rem;
  text-shadow: 0 0 0 transparent;
  transition:
    color 160ms ease,
    text-shadow 160ms ease;
}

.projects-rail__link:hover,
.projects-rail__link:focus-visible {
  color: var(--cream);
  text-shadow: 0 0 18px rgba(196, 0, 26, 0.55);
}

.projects-rail__link[aria-current='page'] {
  color: var(--cream);
  text-shadow: 0 0 22px rgba(196, 0, 26, 0.7);
}

.projects-rail__saber {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 1.85rem;
}

.saber__blade {
  position: relative;
  flex: 1 1 auto;
  width: 11px;
  border-radius: 999px 999px 2px 2px;
  background: linear-gradient(180deg, #ff8a96 0%, #c4001a 16%, #8a0012 100%);
  box-shadow:
    0 0 6px 1px rgba(255, 210, 214, 0.7),
    0 0 16px 5px rgba(196, 0, 26, 0.7),
    0 0 42px 14px rgba(196, 0, 26, 0.32);
  transform-origin: bottom center;
  animation:
    saber-ignite 0.75s ease-out,
    saber-flicker 2.8s ease-in-out infinite 0.75s;
}

.saber__core {
  position: absolute;
  inset: 6% 34%;
  border-radius: inherit;
  background: #fff;
  box-shadow: 0 0 8px 2px rgba(255, 255, 255, 0.85);
}

.saber__bloom {
  position: absolute;
  inset: -8% -140%;
  border-radius: 999px;
  background: radial-gradient(ellipse at 50% 20%, rgba(255, 120, 130, 0.28), transparent 58%);
  pointer-events: none;
  animation: saber-travel 2.6s linear infinite;
}

.saber__blade::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(180deg, transparent 0%, rgba(255, 255, 255, 0.38) 46%, transparent 58%);
  background-size: 100% 240%;
  animation: saber-travel 2.6s linear infinite;
}

.saber__emitter {
  width: 18px;
  height: 14px;
  margin-top: -2px;
  background: linear-gradient(90deg, #1a2028, #8aa6bc, #12161c);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(238, 243, 248, 0.18),
    0 -6px 16px rgba(196, 0, 26, 0.45);
}

.saber__hilt {
  position: relative;
  width: 16px;
  height: 4.4rem;
  background:
    repeating-linear-gradient(180deg, #2a313a 0 7px, #161b22 7px 10px),
    linear-gradient(90deg, #12161c, #3a4452, #12161c);
  border-radius: 1px 1px 5px 5px;
  box-shadow: inset 0 0 0 1px rgba(138, 166, 188, 0.28);
}

.saber__switch {
  position: absolute;
  top: 0.7rem;
  right: -3px;
  width: 6px;
  height: 10px;
  border-radius: 1px;
  background: var(--sith);
  box-shadow: 0 0 10px rgba(196, 0, 26, 0.85);
}

@keyframes saber-ignite {
  from {
    transform: scaleY(0.08);
    opacity: 0.2;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes saber-flicker {
  0%,
  100% {
    filter: brightness(1);
  }

  6% {
    filter: brightness(1.18);
  }

  8% {
    filter: brightness(0.9);
  }

  16% {
    filter: brightness(1.08);
  }

  62% {
    filter: brightness(1);
  }

  71% {
    filter: brightness(1.14);
  }

  73% {
    filter: brightness(0.94);
  }
}

@keyframes saber-travel {
  from {
    background-position: 0 -80%;
  }

  to {
    background-position: 0 180%;
  }
}

@media (min-width: 1100px) {
  .topic-shell--projects {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1.5rem;
  }

  .projects-rail {
    position: sticky;
    top: 6.5rem;
    min-height: min(34rem, 68vh);
    align-self: start;
    order: 0;
  }
}

@media (max-width: 639px) {
  .site-nav {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'brand links'
      'saber saber';
  }

  .site-nav .brand-link {
    grid-area: brand;
  }

  .theme-saber {
    grid-area: saber;
  }

  .site-nav__links {
    grid-area: links;
  }

  .theme-saber__track {
    width: min(14rem, 70vw);
  }
}

@media (min-width: 768px) {
  .site-nav {
    padding: 0.5rem 1.5rem;
  }

  .doors {
    grid-template-columns: 1.35fr 1fr;
  }

  .door--featured {
    grid-column: 1 / -1;
    min-height: 11rem;
    flex-direction: row;
    align-items: flex-end;
  }

  .door--featured .door__label {
    font-size: clamp(3rem, 6vw, 4.4rem);
  }
}

[data-theme='light'] {
  color-scheme: light;
  --night: #e7f0f4;
  --hull: #d5e4ec;
  --cream: #102033;
  --mist: #2c4a5c;
  --dust: #3d5568;
  --steel: #2f7d86;
  --sith: #1c5cff;
  --fg: #102033;
  --glass: rgba(255, 255, 255, 0.7);
  --line: rgba(28, 92, 255, 0.22);
  --on-accent: #f7fbff;
  --btn-fill: rgba(255, 255, 255, 0.82);
  --glow: 28, 92, 255;
}

[data-theme='light'] .atmosphere {
  background:
    radial-gradient(ellipse 50% 40% at 72% 18%, rgba(47, 180, 110, 0.18), transparent 52%),
    radial-gradient(ellipse 60% 45% at 18% 80%, rgba(28, 92, 255, 0.16), transparent 55%),
    linear-gradient(180deg, #d7ebf6 0%, #e7f0f4 48%, #e3f3ea 100%);
}

[data-theme='light'] .atmosphere__stars {
  opacity: 0.35;
}

[data-theme='light'] .atmosphere__station,
[data-theme='light'] .atmosphere__destroyer {
  display: none;
}

[data-theme='light'] .atmosphere__falcon,
[data-theme='light'] .atmosphere__xwing {
  display: block;
}

[data-theme='light'] .atmosphere__falcon {
  filter: drop-shadow(0 0 60px rgba(28, 92, 255, 0.22));
}

[data-theme='light'] .atmosphere__xwing {
  filter: drop-shadow(0 12px 40px rgba(47, 180, 110, 0.2));
}

[data-theme='light'] .atmosphere__visor {
  background:
    radial-gradient(circle at 28% 50%, #1c5cff 0 18%, transparent 42%),
    radial-gradient(circle at 72% 50%, #2fb46e 0 18%, transparent 42%);
}

[data-theme='light'] .wordmark {
  text-shadow: 0 0 48px rgba(28, 92, 255, 0.22);
}

[data-theme='light'] .door::after {
  background: radial-gradient(circle, rgba(28, 92, 255, 0.18), transparent 68%);
}

[data-theme='light'] .door:hover,
[data-theme='light'] .door:focus-visible {
  box-shadow: 0 16px 40px rgba(28, 92, 255, 0.16);
}

[data-theme='light'] .topic-shell::before {
  border-color: rgba(47, 180, 110, 0.22);
  box-shadow: inset 0 0 80px rgba(47, 180, 110, 0.12);
}

[data-theme='light'] .projects-rail::before {
  background: rgba(255, 255, 255, 0.62);
  border-color: rgba(28, 92, 255, 0.28);
}

[data-theme='light'] .projects-rail__link:hover,
[data-theme='light'] .projects-rail__link:focus-visible,
[data-theme='light'] .projects-rail__link[aria-current='page'] {
  text-shadow: 0 0 18px rgba(28, 92, 255, 0.45);
}

[data-theme='light'] .saber__blade {
  background: linear-gradient(180deg, #9ad2ff 0%, #1c5cff 16%, #123a9a 100%);
  box-shadow:
    0 0 6px 1px rgba(210, 232, 255, 0.8),
    0 0 16px 5px rgba(28, 92, 255, 0.65),
    0 0 42px 14px rgba(47, 180, 110, 0.22);
}

[data-theme='light'] .saber__bloom {
  background: radial-gradient(ellipse at 50% 20%, rgba(110, 200, 255, 0.3), transparent 58%);
}

[data-theme='light'] .saber__emitter {
  box-shadow:
    inset 0 0 0 1px rgba(16, 32, 51, 0.12),
    0 -6px 16px rgba(28, 92, 255, 0.4);
}

[data-theme='light'] .theme-saber__hilt--vader {
  display: none;
}

[data-theme='light'] .theme-saber__hilt--luke {
  display: block;
}

[data-theme='light'] .theme-saber__blade {
  background: linear-gradient(90deg, #123a9a 0%, #1c5cff 22%, #9ad2ff 100%);
  box-shadow:
    0 0 6px 1px rgba(210, 232, 255, 0.8),
    0 0 16px 5px rgba(28, 92, 255, 0.7),
    0 0 36px 10px rgba(47, 180, 110, 0.28);
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  .door,
  .btn,
  .theme-saber__blade {
    animation: none;
    transition: none;
  }

  .saber__blade,
  .saber__bloom,
  .saber__blade::after {
    animation: none;
  }

  .xwing__shot,
  .xwing__muzzle,
  .xwing__arc {
    display: none;
    animation: none;
  }

  .theme-flicker--on {
    animation: none;
  }
}
